import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InputCopy } from "@/components/molecules/input-copy";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  foreignKeyPath,
  formatTimestamp,
  humanizeColumn,
  isStatusColumn,
  isTimestampValue,
  timestampTitle,
  looksLikeId,
  orderColumns,
} from "@/features/backoffice/utils/columns";
import type { AdminEntitySchema, AdminRow } from "@/features/backoffice";
import { ArrowSquareOutIcon, FileTextIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * One row, rendered from its schema.
 *
 * Same contract as the grid: the fields, their order and their formatting all
 * come from the table definition, so a detail page never falls behind the
 * entity it shows. The metadata (`id`, who created it and when) is pulled out
 * into a compact bar at the top — on a forty-column row it is the part people
 * read first and the part that reads worst inside a grid of labels.
 */

const METADATA_FIELDS = [
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "is_deleted",
];

function FieldValue({
  name,
  type,
  value,
}: {
  name: string;
  type: string;
  value: unknown;
}) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground italic text-sm">vide</span>;
  }

  if (typeof value === "boolean") {
    return (
      <Badge variant="outline" color={value ? "green" : undefined}>
        {value ? "oui" : "non"}
      </Badge>
    );
  }

  if (isTimestampValue(name, type, value)) {
    return (
      <span className="text-sm" title={timestampTitle(value)}>
        {formatTimestamp(value, true)}
        <span className="ml-2 text-xs text-muted-foreground font-mono">
          {String(value)}
        </span>
      </span>
    );
  }

  if (typeof value === "object") {
    return (
      <details className="group">
        <summary className="cursor-pointer text-sm text-muted-foreground">
          {Array.isArray(value)
            ? `Liste de ${value.length} élément(s)`
            : "Objet JSON"}
        </summary>
        <pre className="mt-1 max-h-80 overflow-auto rounded-sm bg-muted p-2 text-xs whitespace-pre-wrap break-all">
          {JSON.stringify(value, null, 2)}
        </pre>
      </details>
    );
  }

  const text = String(value);

  if (looksLikeId(text) && (name === "id" || name.endsWith("_id"))) {
    const path = foreignKeyPath(name, text);
    return (
      <div className="flex items-center gap-2">
        <InputCopy value={text} size="sm" className="max-w-64" />
        {path && (
          <Link to={path}>
            <ArrowSquareOutIcon className="h-4 w-4 text-primary" />
          </Link>
        )}
      </div>
    );
  }

  if (isStatusColumn(name)) {
    return <Badge variant="outline">{text.replace(/_/g, " ")}</Badge>;
  }

  if (typeof value === "number") {
    return (
      <span className="text-sm tabular-nums">
        {value.toLocaleString("fr-FR")}
      </span>
    );
  }

  if (text.length > 160) {
    return (
      <details>
        <summary className="cursor-pointer text-sm">
          {text.slice(0, 160)}…
        </summary>
        <div className="mt-1 text-sm whitespace-pre-wrap break-words">
          {text}
        </div>
      </details>
    );
  }

  return <span className="text-sm break-words">{text}</span>;
}

export function EntityDetails({
  schema,
  row,
  isLoading,
  title,
}: {
  schema?: AdminEntitySchema;
  row?: AdminRow | null;
  isLoading?: boolean;
  title?: string;
}) {
  if (isLoading || !schema) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-5 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!row) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          Cette ligne n'existe pas (ou plus).
        </CardContent>
      </Card>
    );
  }

  const present = orderColumns(
    Object.keys(schema.columns).filter((name) => name in row),
  );
  const metadata = present.filter((name) => METADATA_FIELDS.includes(name));
  const content = present.filter((name) => !METADATA_FIELDS.includes(name));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileTextIcon className="h-4 w-4" />
          {title || schema.label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {metadata.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-sm bg-muted/50 p-3">
              {metadata.map((name) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {humanizeColumn(name)}
                  </span>
                  <FieldValue
                    name={name}
                    type={schema.columns[name]}
                    value={row[name]}
                  />
                </div>
              ))}
            </div>
            <Separator />
          </>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {content.map((name) => (
            <div key={name} className="space-y-1 min-w-0">
              <div className="text-xs font-medium text-muted-foreground">
                {humanizeColumn(name)}
                <span className="ml-2 font-mono text-[10px] opacity-60">
                  {schema.columns[name]}
                </span>
              </div>
              <div className="min-h-6">
                <FieldValue
                  name={name}
                  type={schema.columns[name]}
                  value={row[name]}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
